import React, { useState, useEffect } from 'react';
import {
  FileText, Plus, Trash2, Save, ChevronRight, ChevronLeft,
  Download, Sparkles, Loader2, Percent, User, X, Check, Mail
} from 'lucide-react';
import { api } from '../../api';
import CustomersPage from '../Customers/CustomersPage';


function Tooltip({ text }) {
  const [show, setShow] = React.useState(false);
  return (
    <span className="relative inline-block ml-1">
      <button type="button" onClick={() => setShow(!show)} onBlur={() => setTimeout(() => setShow(false), 150)}
        className="text-slate-300 hover:text-blue-400 transition-colors align-middle">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
      </button>
      {show && (
        <div className="absolute z-50 bottom-6 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs rounded-xl px-3 py-2 w-52 shadow-xl">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
        </div>
      )}
    </span>
  );
}

const STEP = { NOTES: 'notes', BUILDER: 'builder', PREVIEW: 'preview' };

export default function QuoteEditor({ quoteId, onSaved, onBack }) {
  const [step, setStep] = useState(STEP.NOTES);
  const [loading, setLoading] = useState(!!quoteId);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const [savedId, setSavedId] = useState(quoteId || null);
  const [message, setMessage] = useState('');
  const [selectingCustomer, setSelectingCustomer] = useState(false);
  const [company, setCompany] = useState({});

  const [customer, setCustomer] = useState(null);
  const [manualCustomer, setManualCustomer] = useState({ name: '', contact_person: '', email: '', phone: '' });
  const [quoteNumber, setQuoteNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState([{ id: 1, description: '', quantity: 1, price: 0 }]);
  const [useROT, setUseROT] = useState(false);
  const [laborCost, setLaborCost] = useState(0);
  const [validityDays, setValidityDays] = useState(30);

  useEffect(() => {
    api.getCompany().then(setCompany);
    if (quoteId) {
      api.getQuote(quoteId).then(q => {
        setNotes(q.notes || '');
        setDescription(q.description || '');
        setQuoteNumber(q.quote_number || '');
        setItems(JSON.parse(q.items || '[]') || [{ id: 1, description: '', quantity: 1, price: 0 }]);
        setUseROT(!!q.use_rot);
        setLaborCost(q.labor_cost || 0);
        if (q.customer_id) setCustomer({ id: q.customer_id, name: q.customer_name });
        setLoading(false);
      });
    }
  }, []);

  const calcSubtotal = () => items.reduce((s, i) => s + (i.quantity * i.price), 0);
  const calcVAT = () => calcSubtotal() * 0.25;
  const calcROT = () => useROT ? laborCost * 0.30 : 0;
  const calcTotal = () => calcSubtotal() + calcVAT() - calcROT();

  const updateItem = (id, field, value) =>
    setItems(items.map(i => i.id === id ? { ...i, [field]: value } : i));

  const showMessage = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleSave = async (goTo = null) => {
    setSaving(true);
    const data = {
      customer_id: customer?.id || null,
      quote_number: quoteNumber,
      notes,
      description,
      items,
      use_rot: useROT,
      labor_cost: laborCost,
      subtotal: calcSubtotal(),
      vat: calcVAT(),
      rot_deduction: calcROT(),
      total: calcTotal(),
      validity_days: validityDays,
    };
    try {
      let savedQuote;
      if (savedId) {
        savedQuote = await api.updateQuote(savedId, data);
      } else {
        savedQuote = await api.createQuote(data);
        setSavedId(savedQuote.id);
      }
      showMessage('Offert sparad!');
      if (goTo) setStep(goTo);
    } catch (e) {
      showMessage('Fel: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const generateDescription = async () => {
    if (!notes.trim()) return;
    setAiLoading(true);
    try {
      const { description: text } = await api.generateDescription(notes);
      setDescription(text);
    } catch (e) { showMessage('AI-fel: ' + e.message); }
    finally { setAiLoading(false); }
  };

  const fmt = (n) => Math.round(n).toLocaleString('sv-SE');

  const handleSendToPortal = async () => {
    if (!savedId) {
      showMessage('Spara offerten först!');
      return;
    }
    if (!customer?.id) {
      showMessage('Välj en kund från kundregistret för att maila offerten.');
      return;
    }
    if (!window.confirm('Skicka offerten till kundens e-post?')) return;
    setSendLoading(true);
    try {
      const result = await api.sendQuoteToPortal(savedId);
      setSentTo(result.message || 'Offert skickad!');
      showMessage(result.message || 'Offert skickad!');
    } catch (e) {
      showMessage('Fel: ' + e.message);
    } finally {
      setSendLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setPdfLoading(true);
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });

      const displayCust = customer
        ? { name: customer.name, contact_person: customer.contact_person, email: customer.email, phone: customer.phone }
        : manualCustomer;

      const pageW = 210;
      const margin = 18;
      const contentW = pageW - margin * 2;
      let y = margin;

      const fmt = (n) => Math.round(n).toLocaleString('sv-SE');

      // Hjälpfunktioner
      const text = (str, x, yPos, opts = {}) => {
        if (!str) return;
        doc.text(String(str), x, yPos, opts);
      };

      const line = (yPos, color = [226, 232, 240]) => {
        doc.setDrawColor(...color);
        doc.setLineWidth(0.3);
        doc.line(margin, yPos, margin + contentW, yPos);
      };

      // ---- HEADER ----
      // Logo eller företagsnamn
      if (company.logo && company.logo.startsWith('data:image')) {
        try {
          const ext = company.logo.includes('png') ? 'PNG' : 'JPEG';
          doc.addImage(company.logo, ext, margin, y, 30, 15);
          y += 18;
        } catch (e) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(16);
          doc.setTextColor(15, 23, 42);
          text(company.name, margin, y + 5);
          y += 12;
        }
      } else if (company.name) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(15, 23, 42);
        text(company.name, margin, y + 5);
        y += 12;
      }

      // OFFERT-text (höger)
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(36);
      doc.setTextColor(220, 230, 240);
      text('OFFERT', margin + contentW, margin + 10, { align: 'right' });

      // Datum och nummer (höger)
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.setFont('helvetica', 'bold');
      text('DATUM', margin + contentW, margin + 18, { align: 'right' });
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'bold');
      text(new Date().toLocaleDateString('sv-SE'), margin + contentW, margin + 24, { align: 'right' });

      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.setFont('helvetica', 'bold');
      text('OFFERTNUMMER', margin + contentW, margin + 32, { align: 'right' });
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      text(quoteNumber || 'UTKAST', margin + contentW, margin + 38, { align: 'right' });

      // Leverantörsinfo (vänster)
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.setFont('helvetica', 'bold');
      text('LEVERANTÖR', margin, y + 2);
      y += 7;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      doc.setFontSize(9);
      const companyLines = [
        company.address,
        company.zip && company.city ? company.zip + ' ' + company.city : company.city || company.zip,
        company.org_nr ? 'Org.nr: ' + company.org_nr : null,
        [company.phone, company.email].filter(Boolean).join(' | ')
      ].filter(Boolean);
      companyLines.forEach(l => { text(l, margin, y); y += 5; });

      y = Math.max(y, margin + 45) + 6;

      // ---- MOTTAGARE ----
      line(y);
      y += 6;
      doc.setFontSize(8);
      doc.setTextColor(37, 99, 235);
      doc.setFont('helvetica', 'bold');
      text('MOTTAGARE', margin, y);
      y += 6;
      doc.setFontSize(14);
      doc.setTextColor(15, 23, 42);
      text(displayCust.name, margin, y);
      y += 7;
      if (displayCust.contact_person) {
        doc.setFontSize(10);
        doc.setTextColor(100, 116, 139);
        doc.setFont('helvetica', 'normal');
        text('Att: ' + displayCust.contact_person, margin, y);
        y += 5;
      }
      if (displayCust.email || displayCust.phone) {
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);
        text([displayCust.email, displayCust.phone].filter(Boolean).join('  ·  '), margin, y);
        y += 5;
      }
      y += 4;
      line(y);
      y += 8;

      // ---- BESKRIVNING ----
      if (description) {
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        doc.setFont('helvetica', 'bold');
        text('BESKRIVNING', margin, y);
        y += 6;

        doc.setFontSize(9.5);
        doc.setTextColor(71, 85, 105);
        doc.setFont('helvetica', 'italic');
        const validityDate = new Date();
        validityDate.setDate(validityDate.getDate() + validityDays);
        const descWithValidity = description + '\n\nOfferten gäller i ' + validityDays + ' dagar från ' + new Date().toLocaleDateString('sv-SE');
        const descLines = doc.splitTextToSize(descWithValidity, contentW - 8);
        doc.setFillColor(248, 250, 252);
        doc.rect(margin, y - 3, contentW, descLines.length * 5.5 + 8, 'F');
        doc.setDrawColor(203, 213, 225);
        doc.setLineWidth(1);
        doc.line(margin, y - 3, margin, y - 3 + descLines.length * 5.5 + 8);
        doc.setLineWidth(0.3);
        descLines.forEach(l => { text(l, margin + 6, y); y += 5.5; });
        y += 10;
      }

      // ---- RADER ----
      // Rubrikrad
      doc.setFillColor(15, 23, 42);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(255, 255, 255);
      doc.rect(margin, y - 4, contentW, 10, 'F');
      text('BESKRIVNING', margin + 3, y + 2);
      text('ANTAL', margin + contentW * 0.62, y + 2, { align: 'right' });
      text('PRIS/ST', margin + contentW * 0.78, y + 2, { align: 'right' });
      text('BELOPP', margin + contentW, y + 2, { align: 'right' });
      y += 10;

      // Rader
      const filteredItems = items.filter(i => i.description);
      filteredItems.forEach((item, idx) => {
        if (idx % 2 === 0) {
          doc.setFillColor(248, 250, 252);
          doc.rect(margin, y - 4, contentW, 9, 'F');
        }
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(30, 41, 59);
        text(item.description, margin + 3, y + 1);
        doc.setTextColor(71, 85, 105);
        text(String(item.quantity), margin + contentW * 0.62, y + 1, { align: 'right' });
        text(fmt(item.price) + ' kr', margin + contentW * 0.78, y + 1, { align: 'right' });
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 23, 42);
        text(fmt(item.quantity * item.price) + ' kr', margin + contentW, y + 1, { align: 'right' });
        y += 9;
      });

      y += 6;

      // ---- SUMMERING ----
      const sumX = margin + contentW * 0.55;
      const sumW = contentW * 0.45;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(148, 163, 184);

      const addSumRow = (label, value, bold = false, color = null) => {
        if (bold) doc.setFont('helvetica', 'bold'); else doc.setFont('helvetica', 'normal');
        if (color) doc.setTextColor(...color); else doc.setTextColor(148, 163, 184);
        doc.setFontSize(9);
        text(label.toUpperCase(), sumX, y);
        text(value, margin + contentW, y, { align: 'right' });
        y += 6;
      };

      addSumRow('Subtotal:', fmt(calcSubtotal()) + ' kr');
      addSumRow('Moms (25%):', fmt(calcVAT()) + ' kr');
      if (useROT) {
        y += 1;
        addSumRow('ROT-avdrag:', '-' + fmt(calcROT()) + ' kr', true, [37, 99, 235]);
        y += 1;
      }

      // Totalrad
      line(y, [15, 23, 42]);
      doc.setDrawColor(15, 23, 42);
      doc.setLineWidth(0.8);
      doc.line(sumX - 2, y, margin + contentW, y);
      doc.setLineWidth(0.3);
      y += 8;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(15, 23, 42);
      text('TOTALT:', sumX, y);
      text(fmt(calcTotal()) + ' kr', margin + contentW, y, { align: 'right' });

      // ---- SIGNATUR ----
      y += 14;
      const sigY = Math.max(y, 230);
      doc.setDrawColor(203, 213, 225);
      doc.setLineWidth(0.4);
      // Leverantörens signatur
      doc.line(margin, sigY, margin + 70, sigY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      text('Leverantörens underskrift', margin, sigY + 5);
      text('Datum', margin, sigY + 11);
      // Kundens signatur
      doc.line(margin + contentW - 70, sigY, margin + contentW, sigY);
      text('Kundens underskrift', margin + contentW - 70, sigY + 5);
      text('Datum', margin + contentW - 70, sigY + 11);

      // ---- FOOTER ----
      const footerY = 285;
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(margin, footerY - 4, margin + contentW, footerY - 4);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(148, 163, 184);
      const footerParts = [
        company.name || '',
        company.org_nr ? 'Org: ' + company.org_nr : null,
        company.email || null,
        company.phone || null
      ].filter(Boolean);
      const footerSpacing = contentW / Math.max(footerParts.length - 1, 1);
      footerParts.forEach((item, i) => {
        const align = i === 0 ? 'left' : i === footerParts.length - 1 ? 'right' : 'center';
        const x = i === 0 ? margin : i === footerParts.length - 1 ? margin + contentW : margin + i * footerSpacing;
        text(item.toUpperCase(), x, footerY, { align });
      });

      doc.save('Offert-' + (quoteNumber || displayCust.name || 'iOffer') + '.pdf');
    } catch (e) {
      console.error('PDF error:', e);
      showMessage('PDF-fel: ' + e.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const displayCustomer = customer
    ? { name: customer.name, contact_person: customer.contact_person, email: customer.email, phone: customer.phone }
    : manualCustomer;

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-600" size={32} /></div>;

  if (selectingCustomer) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-black">Välj kund</h3>
          <button onClick={() => setSelectingCustomer(false)}><X size={24} /></button>
        </div>
        <CustomersPage selectMode onSelectCustomer={(c) => {
          setCustomer(c);
          setManualCustomer({ name: '', contact_person: '', email: '', phone: '' });
          setSelectingCustomer(false);
        }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Steg-indikator */}
      <div className="flex items-center gap-2 mb-6">
        {[STEP.NOTES, STEP.BUILDER, STEP.PREVIEW].map((s, i) => (
          <React.Fragment key={s}>
            <button onClick={() => setStep(s)}
              className={`px-4 py-2 rounded-xl text-sm font-black transition-all
                ${step === s ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-slate-400 border border-slate-200'}`}>
              {i + 1}. {s === STEP.NOTES ? 'Anteckningar' : s === STEP.BUILDER ? 'Offert' : 'Granska'}
            </button>
            {i < 2 && <ChevronRight className="text-slate-300" size={16} />}
          </React.Fragment>
        ))}
        <button onClick={onBack} className="ml-auto text-slate-400 flex items-center gap-1 text-sm"><X size={16} /> Stäng</button>
      </div>

      {message && (
        <div className="fixed top-4 right-4 z-50 bg-green-50 border border-green-200 text-green-800 px-5 py-3 rounded-xl shadow-xl font-bold flex items-center gap-2">
          <Check size={18} /> {message}
        </div>
      )}

      {/* STEG 1: ANTECKNINGAR */}
      {step === STEP.NOTES && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="text-xl font-black mb-4 flex items-center gap-2"><User className="text-blue-600" size={20} /> Kundinformation</h2>
            {customer ? (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between mb-4">
                <div>
                  <p className="font-black text-blue-900">{customer.name}</p>
                  {customer.contact_person && <p className="text-sm text-blue-700">{customer.contact_person}</p>}
                </div>
                <button onClick={() => setCustomer(null)} className="text-blue-400 hover:text-blue-700"><X size={18} /></button>
              </div>
            ) : null}
            <div className="flex gap-3 mb-4">
              <button onClick={() => setSelectingCustomer(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2">
                <User size={16} /> Välj från kundregister
              </button>
            </div>
            {!customer && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Kundnamn</label>
                  <input value={manualCustomer.name} onChange={e => setManualCustomer({...manualCustomer, name: e.target.value})}
                    className="w-full mt-1 p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500" placeholder="Kundnamn AB" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Offertnummer</label>
                  <input value={quoteNumber} onChange={e => setQuoteNumber(e.target.value)}
                    className="w-full mt-1 p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-mono" placeholder="2026-001" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Kontaktperson</label>
                  <input value={manualCustomer.contact_person} onChange={e => setManualCustomer({...manualCustomer, contact_person: e.target.value})}
                    className="w-full mt-1 p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">E-post</label>
                  <input value={manualCustomer.email} onChange={e => setManualCustomer({...manualCustomer, email: e.target.value})}
                    className="w-full mt-1 p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase">Telefon</label>
                  <input value={manualCustomer.phone} onChange={e => setManualCustomer({...manualCustomer, phone: e.target.value})}
                    className="w-full mt-1 p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            )}
            {customer && (
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase">Offertnummer</label>
                <input value={quoteNumber} onChange={e => setQuoteNumber(e.target.value)}
                  className="w-full mt-1 p-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-mono" placeholder="2026-001" />
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="text-xl font-black mb-4 flex items-center gap-2"><FileText className="text-blue-600" size={20} /> Mötesanteckningar<Tooltip text="Skriv fritt vad som diskuterades. AI-verktyget använder dessa anteckningar för att generera en professionell offerttext." /></h2>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full h-64 p-4 border border-slate-200 rounded-xl outline-none bg-slate-50 focus:bg-white transition-all text-sm resize-none"
              placeholder="Vad kom ni överens om på mötet? Skriv fritt..." />
          </div>

          <div className="flex justify-between">
            <button onClick={() => handleSave()} disabled={saving}
              className="bg-slate-100 px-6 py-3 rounded-xl font-bold flex items-center gap-2">
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Spara utkast
            </button>
            <button onClick={() => setStep(STEP.BUILDER)}
              className="bg-blue-600 text-white px-6 py-3 rounded-xl font-black flex items-center gap-2 shadow-lg">
              Nästa <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* STEG 2: BYGGARE */}
      {step === STEP.BUILDER && (
        <div className="space-y-6">
          {notes && (
            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex justify-between items-center">
              <p className="text-blue-700 text-sm italic line-clamp-2 flex-1 mr-4">{notes}</p>
              <button onClick={generateDescription} disabled={aiLoading}
                className="bg-white border border-blue-200 text-blue-600 px-4 py-2 rounded-xl text-sm font-black flex items-center gap-2 flex-shrink-0">
                {aiLoading ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />} AI-text
              </button>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <label className="text-xs font-bold text-slate-500 uppercase flex items-center">Inledningstext<Tooltip text="En kort beskrivning av uppdraget som visas i offerten. Kan genereras automatiskt med AI baserat på dina anteckningar." /></label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              className="w-full mt-2 h-24 p-3 border border-slate-200 rounded-xl outline-none text-sm italic resize-none"
              placeholder="Beskriv tjänsten/uppdraget..." />

            <h2 className="text-xl font-black my-6 flex items-center gap-2 border-t pt-6">Offertrader</h2>
            <div className="space-y-3">
              <div className="grid grid-cols-12 gap-2 text-[10px] font-black text-slate-400 uppercase px-3">
                <div className="col-span-6">Beskrivning</div>
                <div className="col-span-2 text-right">Antal</div>
                <div className="col-span-3 text-right">Pris (kr)</div>
                <div className="col-span-1"></div>
              </div>
              {items.map(item => (
                <div key={item.id} className="grid grid-cols-12 gap-2 items-center bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <div className="col-span-6">
                    <input value={item.description} onChange={e => updateItem(item.id, 'description', e.target.value)}
                      className="w-full p-2 border border-slate-200 rounded-lg text-sm bg-white" placeholder="Tjänst / produkt" />
                  </div>
                  <div className="col-span-2">
                    <input type="number" value={item.quantity} onChange={e => updateItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                      className="w-full p-2 border border-slate-200 rounded-lg text-sm text-right bg-white" />
                  </div>
                  <div className="col-span-3">
                    <input type="number" value={item.price} onChange={e => updateItem(item.id, 'price', parseFloat(e.target.value) || 0)}
                      className="w-full p-2 border border-slate-200 rounded-lg text-sm text-right bg-white" />
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <button onClick={() => setItems(items.length > 1 ? items.filter(i => i.id !== item.id) : items)}
                      className="text-slate-300 hover:text-red-500"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
              <button onClick={() => setItems([...items, { id: Date.now(), description: '', quantity: 1, price: 0 }])}
                className="w-full py-3 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 flex items-center justify-center gap-2 hover:border-blue-300 hover:text-blue-500">
                <Plus size={16} /> Lägg till rad
              </button>
            </div>

            {/* ROT */}
            <div className="mt-8 pt-6 border-t">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl ${useROT ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}><Percent size={18} /></div>
                  <div>
                    <h4 className="font-black flex items-center">ROT-avdrag<Tooltip text="ROT-avdrag ger kunden 30% skattereduktion på arbetskostnaden. Gäller för hushållsnära tjänster som rot, rut m.m." /></h4>
                    <p className="text-xs text-slate-500">30% avdrag på arbetskostnad</p>
                  </div>
                </div>
                <input type="checkbox" checked={useROT} onChange={e => setUseROT(e.target.checked)} className="w-5 h-5 accent-blue-600" />
              </div>
              {useROT && (
                <div className="mt-3">
                  <label className="text-xs font-bold text-slate-500 uppercase flex items-center">Arbetskostnad inkl. moms<Tooltip text="Ange den del av totalbeloppet som avser arbete (inkl. moms). ROT-avdraget blir 30% av detta belopp." /></label>
                  <input type="number" value={laborCost} onChange={e => setLaborCost(parseFloat(e.target.value) || 0)}
                    className="w-full mt-1 p-3 border border-blue-200 rounded-xl outline-none" />
                </div>
              )}
            </div>

            {/* Giltighetstid */}
            <div className="mt-6 pt-6 border-t">
              <label className="text-xs font-bold text-slate-500 uppercase flex items-center">Offertens giltighetstid<Tooltip text="Hur länge offerten är giltig från skapad datum. Kunden kan inte svara på offerten efter att den gått ut." /></label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {[14, 30, 45, 60].map(d => (
                  <button key={d} type="button"
                    onClick={() => setValidityDays(d)}
                    className={`px-4 py-2 rounded-xl text-sm font-black border transition-all
                      ${validityDays === d ? 'bg-blue-600 text-white border-blue-600' : 'bg-slate-50 text-slate-500 border-slate-200 hover:border-blue-300'}`}>
                    {d} dagar
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 border-t pt-6 flex justify-end">
              <div className="text-right space-y-1">
                <p className="text-slate-400 text-sm">Netto: {calcSubtotal().toLocaleString('sv-SE')} kr</p>
                <p className="text-slate-400 text-sm">Moms (25%): {calcVAT().toLocaleString('sv-SE')} kr</p>
                {useROT && <p className="text-blue-600 text-sm font-bold">ROT-avdrag: -{calcROT().toLocaleString('sv-SE')} kr</p>}
                <p className="text-3xl font-black text-slate-900 pt-2">TOTALT: {Math.round(calcTotal()).toLocaleString('sv-SE')} kr</p>
              </div>
            </div>
          </div>

          <div className="flex justify-between pb-10">
            <button onClick={() => setStep(STEP.NOTES)} className="text-slate-500 flex items-center gap-2"><ChevronLeft size={18} /> Tillbaka</button>
            <div className="flex gap-3">
              <button onClick={() => handleSave()} disabled={saving}
                className="bg-slate-100 px-6 py-3 rounded-xl font-bold flex items-center gap-2">
                {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Spara
              </button>
              <button onClick={() => handleSave(STEP.PREVIEW)}
                className="bg-blue-600 text-white px-6 py-3 rounded-xl font-black shadow-lg flex items-center gap-2">
                Granska <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEG 3: FÖRHANDSVISNING */}
      {step === STEP.PREVIEW && (
        <div className="space-y-6 pb-20">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden max-w-[700px] mx-auto">
            {/* Header */}
            <div className="p-10 pb-0">
              <div className="flex justify-between items-start mb-10">
                <div>
                  {company.logo
                    ? <img src={company.logo} alt="Logo" className="h-14 object-contain mb-4" />
                    : <div className="text-2xl font-black mb-3">{company.name}</div>
                  }
                  <div className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-2">Leverantör</div>
                  <div className="text-xs text-slate-500 leading-6">
                    {company.address && <div>{company.address}</div>}
                    {(company.zip || company.city) && <div>{company.zip} {company.city}</div>}
                    {company.org_nr && <div>Org.nr: {company.org_nr}</div>}
                    {(company.phone || company.email) && <div>Tel: {company.phone}{company.email ? ` | ${company.email}` : ''}</div>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-6xl font-black text-slate-100 leading-none mb-4">OFFERT</div>
                  <div className="text-[9px] font-black uppercase text-slate-400 tracking-widest">Datum</div>
                  <div className="font-bold mb-3">{new Date().toLocaleDateString('sv-SE')}</div>
                  <div className="text-[9px] font-black uppercase text-slate-400 tracking-widest">Offertnummer</div>
                  <div className="font-bold">{quoteNumber || 'UTKAST'}</div>
                </div>
              </div>

              {/* Mottagare */}
              <div className="border-y border-slate-100 py-6 mb-8">
                <div className="text-[9px] font-black uppercase text-blue-600 tracking-widest mb-2">Mottagare</div>
                <div className="text-xl font-black">{displayCustomer.name || '—'}</div>
                {displayCustomer.contact_person && <div className="text-sm text-slate-500 mt-1">Att: {displayCustomer.contact_person}</div>}
                <div className="text-xs text-slate-400 mt-1">
                  {displayCustomer.email && <span>{displayCustomer.email}</span>}
                  {displayCustomer.email && displayCustomer.phone && <span> · </span>}
                  {displayCustomer.phone && <span>{displayCustomer.phone}</span>}
                </div>
              </div>

              {/* Beskrivning */}
              {description && (
                <div className="mb-8">
                  <div className="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-2">Beskrivning</div>
                  <div className="text-sm italic text-slate-600 bg-slate-50 p-5 rounded-xl border-l-4 border-slate-200 whitespace-pre-wrap leading-relaxed">{description}{`\n\nOfferten gäller i ${validityDays} dagar från ${new Date().toLocaleDateString('sv-SE')}`}</div>
                </div>
              )}

              {/* Rader */}
              <table className="w-full border-collapse mb-8">
                <thead>
                  <tr className="border-b-2 border-slate-900">
                    <th className="py-3 text-left text-[10px] font-black uppercase tracking-wider">Beskrivning</th>
                    <th className="py-3 text-right text-[10px] font-black uppercase tracking-wider">Antal</th>
                    <th className="py-3 text-right text-[10px] font-black uppercase tracking-wider">Pris/st</th>
                    <th className="py-3 text-right text-[10px] font-black uppercase tracking-wider">Belopp</th>
                  </tr>
                </thead>
                <tbody>
                  {items.filter(i => i.description).map(item => (
                    <tr key={item.id} className="border-b border-slate-100">
                      <td className="py-4 text-sm">{item.description}</td>
                      <td className="py-4 text-right text-sm">{item.quantity}</td>
                      <td className="py-4 text-right text-sm">{item.price.toLocaleString('sv-SE')} kr</td>
                      <td className="py-4 text-right text-sm font-black">{(item.quantity * item.price).toLocaleString('sv-SE')} kr</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Summering */}
              <div className="flex justify-end mb-10">
                <div className="w-72 space-y-2">
                  <div className="flex justify-between text-[10px] font-black uppercase text-slate-400">
                    <span>Subtotal:</span><span>{calcSubtotal().toLocaleString('sv-SE')} kr</span>
                  </div>
                  <div className="flex justify-between text-[10px] font-black uppercase text-slate-400 border-b border-slate-100 pb-3">
                    <span>Moms (25%):</span><span>{calcVAT().toLocaleString('sv-SE')} kr</span>
                  </div>
                  {useROT && (
                    <div className="flex justify-between text-blue-600 font-black text-sm uppercase">
                      <span>ROT-avdrag:</span><span>-{calcROT().toLocaleString('sv-SE')} kr</span>
                    </div>
                  )}
                  <div className="flex justify-between font-black text-3xl border-t-4 border-slate-900 pt-4">
                    <span>Totalt:</span><span>{Math.round(calcTotal()).toLocaleString('sv-SE')} kr</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 border-t border-slate-100 px-4 sm:px-10 py-3 flex flex-wrap gap-x-4 gap-y-1 justify-center sm:justify-between text-[9px] text-slate-400 font-black uppercase tracking-widest">
              <span>{company.name}</span>
              <span>{company.org_nr ? `Org: ${company.org_nr}` : ''}</span>
              <span>{company.email}</span>
              <span>{company.phone}</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3">
            <button onClick={() => setStep(STEP.BUILDER)}
              className="bg-slate-200 px-6 py-3 rounded-xl font-bold flex items-center gap-2">
              <ChevronLeft size={18} /> Redigera
            </button>
            <button onClick={handleExportPDF} disabled={pdfLoading}
              className="bg-green-600 text-white px-6 py-3 sm:py-4 rounded-xl font-black text-base sm:text-lg flex items-center justify-center gap-2 shadow-xl">
              {pdfLoading ? <Loader2 className="animate-spin" size={24} /> : <Download size={24} />}
              {pdfLoading ? 'Genererar PDF...' : 'Ladda ner PDF'}
            </button>
            <button onClick={handleSendToPortal} disabled={sendLoading}
              className="bg-blue-600 text-white px-6 py-3 sm:py-4 rounded-xl font-black text-base sm:text-lg flex items-center justify-center gap-2 shadow-xl">
              {sendLoading ? <Loader2 className="animate-spin" size={24} /> : <Mail size={24} />}
              {sendLoading ? 'Skickar...' : 'Maila offert till kund'}
            </button>
          </div>
          {sentTo && (
            <div className="text-center bg-blue-50 border border-blue-200 text-blue-800 px-5 py-3 rounded-xl font-bold text-sm">
              ✉️ {sentTo}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
